import { Service } from "../../../config/service";

// Relatórios já migrados para a P84-API (Gotenberg, VUE_APP_HTTP_ROOT_API_P84).
// Os demais continuam no endpoint antigo do pitom-api (Jasper).
const P84_REPORTS = [
  "PITOM84_EXPORT_NEW_APPRAISALS_FOR_SELLER",
  "PITOM84_EXPORT_CAPTURE_FOR_SELLER",
  "PITOM84_EXPORT_NEW_APPRAISALS_BY_STORE",
  "PITOM84_EXPORT_CAPTURE_BY_STORE",
  "PITOM84_EXPORT_CUSTOMERS_APPRAISALS",
];
const REPORT_FORMAT_SUFFIX = /_(PDF|XLS)$/;
const EXPORT_ERROR_MESSAGE = "Não foi possível exportar o relatório";

class ExportFileService extends Service {
  constructor() {
    super("report/export/file");
  }

  isP84Report(reportName) {
    return P84_REPORTS.includes(
      String(reportName).replace(REPORT_FORMAT_SUFFIX, "")
    );
  }

  /**
   * Exporta pela P84-API: type=pdf gera PDF e type=xls gera CSV.
   * As lojas permitidas são aplicadas pela própria API conforme a sessão.
   */
  async fetchReportP84(type, reportName, file_name, params = {}) {
    let startDate = params.START_DATE;
    let endDate = params.END_DATE || startDate;
    if (startDate && endDate && startDate > endDate) {
      [startDate, endDate] = [endDate, startDate];
    }

    this.setParam("type", type);
    this.setParam("report", reportName);
    this.setParam(
      "param",
      JSON.stringify({
        ignorePagination: type != "pdf",
        onePagePerSheet: false,
        ...params,
        START_DATE: startDate,
        END_DATE: endDate,
      })
    );

    let response;
    try {
      response = await this.http84.get(this.getResource().list, {
        params: this.getParams(),
        responseType: "blob",
      });
    } catch (e) {
      throw new Error(await this.readBlobErrorMessage(e));
    }

    const contentType = response.headers["content-type"] || "";
    const extension = contentType.includes("text/csv") ? "csv" : "pdf";
    this.downloadBlob(response.data, `${file_name}.${extension}`);
  }

  /** Com responseType "blob" o corpo do erro também chega como Blob. */
  async readBlobErrorMessage(e) {
    const data = e?.response?.data;
    if (!(data instanceof Blob)) return e?.message || EXPORT_ERROR_MESSAGE;
    try {
      const body = JSON.parse(await data.text());
      const message = body?.message?.message ?? body?.message;
      return (
        (Array.isArray(message) ? message.join(", ") : message) ||
        EXPORT_ERROR_MESSAGE
      );
    } catch (parseError) {
      return EXPORT_ERROR_MESSAGE;
    }
  }

  downloadBlob(blob, fileName) {
    const fileUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(fileUrl);
    link.remove();
  }

  /**
   * Avaliações por status (status_appraisal_report) pela P84-API. Os nomes do
   * Jasper (p_store_id, p_team_id, p_user_id, dateB, dateD) viram os filtros
   * padrão da rota nova.
   */
  async fetch_PITOM84_EXPORT_STORE_SELLER_STATUS(
    type,
    store_name,
    { store_id, team_id, user_id, p_inputdate_init, p_inputdate_last }
  ) {
    return this.fetchReportP84(type, "status_appraisal_report", store_name, {
      START_DATE: p_inputdate_init,
      END_DATE: p_inputdate_last,
      STORES: store_id,
      USER: user_id,
      TEAMS: team_id,
    });
  }

  async fetchReport(type, reportName, file_name, params) {
    if (this.isP84Report(reportName)) {
      return this.fetchReportP84(type, reportName, file_name, params);
    }

    try {
      const ignorepagination = type == "xls" ? true : false;
      this.setParam("type", type);
      this.setParam("report", reportName);
      this.setParam("param", {
        ignorePagination: ignorepagination,
        onePagePerSheet: false,
        ...params
      });
      // { store_id: 319, p_inputdate_init: "2023-01-01", p_inputdate_last: "2023-01-27" }

      const { data } = await this.list({
        responseType: "blob",
      });
      const file = new Blob([data], { type: `application/${type}` });
      const fileURL = URL.createObjectURL(file);
      // window.open(fileURL)

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `${file_name}.${type}`);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      console.error(e);
      this.$toast.error(e.message);
    } finally {
      this.loading = false;
    }
  }

  async fetch_PITOM84_EXPORT_CLIENT_XLS(
    type,
    store_name,
    { store_id, team_id, user_id, p_inputdate_init, p_inputdate_last }
  ) {
    try {
      const ignorepagination = type == "xls" ? true : false;
      this.setParam("type", type);
      this.setParam("report", "PITOM84_EXPORT_CLIENT_XLS");
      this.setParam("param", {
        ignorePagination: ignorepagination,
        onePagePerSheet: false,
        store_id: store_id,
        team_id: team_id,
        databegin: p_inputdate_init,
        datadone: p_inputdate_last,
      });
      // { store_id: 319, p_inputdate_init: "2023-01-01", p_inputdate_last: "2023-01-27" }

      const { data } = await this.list({
        responseType: "blob",
      });

      const file = new Blob([data], { type: `application/${type}` });
      const fileURL = URL.createObjectURL(file);
      // window.open(fileURL)

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `${store_name}.${type}`);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      console.error(e);
      this.$toast.error(e.message);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Relatório de Clientes pela P84-API: type=pdf gera PDF e type=xls gera CSV.
   * Lojas fora da sessão são descartadas pela API; só Sudo, Administrador e
   * Gerente podem exportar.
   */
  async fetch_PITOM84_EXPORT_CUSTOMERS_APPRAISALS_XLS(type, params) {
    return this.fetchReportP84(
      type,
      "PITOM84_EXPORT_CUSTOMERS_APPRAISALS_XLS",
      "Relatório de Clientes",
      {
        START_DATE: params.startDate,
        END_DATE: params.endDate,
        STORES: params.stores,
        SKETCH: params.sketch,
        STATUS: params.status,
      }
    );
  }
}

export default ExportFileService;
